import React from 'react';
import {
  Dialog, Box, Typography, IconButton, Button, Skeleton, Divider, Chip, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LayersIcon from '@mui/icons-material/Layers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PublicIcon from '@mui/icons-material/Public';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { setGameStatus } from '../../services/api';
import { STATUS_COLOR } from '../../constants/statusColors';
import { fetchNewsDetail, sendNewsEvent } from '../../services/newsApi';
import type { NewsArticle, NewsDetailGame } from '../../types/News';

interface NewsDetailSheetProps {
  articleId: number | null;
  onClose: () => void;
  bookmarked: boolean;
  /** 開いた時点で既読になる。ここから個別に取り消せる */
  seen: boolean;
  onToggleSeen: (articleId: number) => void;
  onToggleBookmark: (article: NewsArticle) => void;
  onOpenRelated: (articleId: number) => void;
}

// このプロジェクトは index.css で html { font-size: 20px } を指定している。
// つまり 1rem = 20px。16px 前提で rem を置くと 25% 大きく出る。
//
// **必ず { xs, sm } の対で書くこと。** 単一値だと theme.ts の variant に付いている
// @media (max-width:600px) が勝ってしまい、スマホだけ指定が効かない。
// GameCard / ScoresSection など既存箇所も全てこの形になっている。
const FS = {
  meta: { xs: '0.55rem', sm: '0.6rem' },
  title: { xs: '0.8rem', sm: '0.85rem' },
  summary: { xs: '0.6rem', sm: '0.65rem' },
  label: { xs: '0.55rem', sm: '0.6rem' },
  gameTitle: { xs: '0.65rem', sm: '0.7rem' },
  tag: { xs: '0.5rem', sm: '0.55rem' },
  note: { xs: '0.5rem', sm: '0.55rem' },
} as const;

const STATUS_LABELS: Record<number, string> = {
  1: 'プレイ済み', 2: 'プレイ中', 3: '中断', 4: '積みゲー', 5: 'ウィッシュ',
};

const WISHLIST = 5;

// 詳細ページ(StoreSection)と同じアイコンを使う
const STORES: { key: string; label: string; icon: string | null }[] = [
  { key: 'steam', label: 'Steam', icon: '/icons/service/steam.svg' },
  { key: 'ps', label: 'PlayStation Store', icon: '/icons/service/playstation_color.svg' },
  { key: 'xbox', label: 'Xbox', icon: '/icons/service/xbox_color.svg' },
  { key: 'website', label: '公式サイト', icon: null },
];

/** 記事に紐づいたゲーム1件 */
const GameRow: React.FC<{ game: NewsDetailGame }> = ({ game }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();
  const [status, setStatus] = React.useState<number | null>(game.user_status);
  const [saving, setSaving] = React.useState(false);
  const title = game.japanese_name || game.name;
  // STATUS_COLOR は数値リテラルキーの as const なので number では引けない
  const statusColor = status != null
    ? (STATUS_COLOR as Record<number, string | undefined>)[status]
    : undefined;

  const toggleWishlist = async () => {
    setSaving(true);
    const next = status === WISHLIST ? null : WISHLIST;
    try {
      await setGameStatus(game.game_id, next);
      setStatus(next);
    } catch {
      /* 失敗時は元の表示のまま */
    } finally {
      setSaving(false);
    }
  };

  return (
    // alignItems を flex-start にしないと、行が高いときに
    // カバーが縦へ引き伸ばされて aspectRatio が効かなくなる
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5 }}>
      <Box
        onClick={() => navigate(`/game/${game.game_id}`)}
        sx={{
          width: 60,
          flexShrink: 0,
          aspectRatio: '3 / 4',   // ライブラリのカバーと同じ比率
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'rgba(255,255,255,0.06)',
        }}
      >
        {game.cover && (
          <Box
            component="img" src={game.cover} alt="" loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          onClick={() => navigate(`/game/${game.game_id}`)}
          sx={{
            fontSize: FS.gameTitle, fontWeight: 600, lineHeight: 1.4,
            cursor: 'pointer', '&:hover': { color: 'primary.main' },
          }}
        >
          {title}
        </Typography>

        {game.platforms.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.5 }}>
            {game.platforms.map((p) => (
              <Box
                key={p}
                sx={{
                  px: 0.55, py: 0.1, borderRadius: 0.5, fontSize: FS.tag,
                  color: 'rgba(255,255,255,0.55)',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                {p}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
          {/* ストアはアイコンだけ。文字で並べると幅を食う */}
          {STORES.map(({ key, label, icon }) => {
            const url = (game.store_links as Record<string, string | undefined>)[key];
            if (!url) return null;
            return (
              <Tooltip key={key} title={label}>
                <IconButton
                  size="small"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 28, height: 28,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  {icon
                    ? <Box component="img" src={icon} alt="" sx={{ width: 15, height: 15 }} />
                    : <PublicIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.7)' }} />}
                </IconButton>
              </Tooltip>
            );
          })}

          <Box sx={{ flex: 1 }} />

          {isAuthenticated && (
            // 既に別のステータスが付いているゲームは、うっかり上書きしないよう
            // 現在の状態を出すだけにする。ウィッシュ操作は未登録かウィッシュ時のみ
            status !== null && status !== WISHLIST ? (
              <Chip
                label={STATUS_LABELS[status] || '登録済み'}
                size="small"
                sx={{
                  height: 22, fontSize: FS.tag,
                  bgcolor: statusColor ? `${statusColor}22` : undefined,
                  color: statusColor || 'text.secondary',
                }}
              />
            ) : (
              <Button
                size="small"
                variant={status === WISHLIST ? 'contained' : 'outlined'}
                disabled={saving}
                onClick={toggleWishlist}
                startIcon={status === WISHLIST
                  ? <StarIcon sx={{ fontSize: 14 }} />
                  : <StarBorderIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: FS.tag, py: 0.25, minWidth: 0 }}
              >
                {status === WISHLIST ? 'ウィッシュ済み' : 'ウィッシュ'}
              </Button>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * 記事を開く前のクッション。
 *
 * カードから直接外部サイトへ飛ばすと、紐づいたゲームに触れる機会が無いまま
 * 離脱してしまう。ここで大きな画像・全文のタイトル・ゲーム情報を挟み、
 * 「記事を読む」で初めて外部へ送る。
 */
const NewsDetailSheet: React.FC<NewsDetailSheetProps> = ({
  articleId, onClose, bookmarked, seen, onToggleBookmark, onToggleSeen, onOpenRelated,
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['news-detail', articleId],
    queryFn: () => fetchNewsDetail(articleId as number),
    enabled: articleId != null,
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => { setImageFailed(false); }, [articleId]);

  const article = data?.article;

  const openArticle = () => {
    if (!article) return;
    sendNewsEvent(article.id, 'click');
    window.open(article.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={articleId != null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          // 画像とフッターを固定し、本文だけを1つのスクロール領域にする。
          // ゲーム欄に個別のスクロールを持たせると、タイトルが長いときに
          // ゲーム欄だけが潰れて非常に見づらくなる
          display: 'flex',
          flexDirection: 'column',
          // MUI の既定は上下左右 32px。読み物なのでスマホでは目一杯使う
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: 'calc(100% - 16px)', sm: '88vh' },
          borderRadius: 3,
          backgroundColor: 'rgba(20,20,20,0.96)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.12)',
          backgroundImage: 'none',
        },
      }}
    >
      {/* 公開停止中は HTTP 200 で article: null が返る。
          !article だけで判定するとスケルトンのまま固まる */}
      {/* 保存タブからは消えた記事を開きうる（ブックマークは端末に残り続けるため）。
          404 のときスケルトンで固まらないよう、取得失敗を明示的に扱う */}
      {!isLoading && isError ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: FS.summary, color: 'rgba(255,255,255,0.6)' }}>
            この記事は取得できませんでした
          </Typography>
          <Typography sx={{ fontSize: FS.note, color: 'rgba(255,255,255,0.35)', mt: 0.75 }}>
            配信元で削除されたか、一時的に取得できない状態です
          </Typography>
          <Button size="small" onClick={onClose} sx={{ mt: 2 }}>閉じる</Button>
        </Box>
      ) : !isLoading && data && !article ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: FS.summary, color: 'rgba(255,255,255,0.6)' }}>
            {data.message || 'この記事は表示できません'}
          </Typography>
          <Button size="small" onClick={onClose} sx={{ mt: 2 }}>閉じる</Button>
        </Box>
      ) : isLoading || !article ? (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" sx={{ width: '100%', aspectRatio: '16/9', borderRadius: 2 }} />
          <Skeleton width="90%" height={24} sx={{ mt: 1.5 }} />
          <Skeleton width="60%" height={24} />
        </Box>
      ) : (
        <>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box sx={{ width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)' }}>
              {article.image && !imageFailed ? (
                <Box
                  component="img"
                  src={article.image.url}
                  alt=""
                  referrerPolicy="no-referrer"
                  onError={() => {
                    setImageFailed(true);
                    sendNewsEvent(article.id, 'image_error');
                  }}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Box sx={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.18)',
                }}>
                  <NewspaperIcon sx={{ fontSize: 44 }} />
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                position: 'absolute', top: 8, right: 8, width: 30, height: 30,
                bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                color: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: FS.meta, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
                  {article.source.name}
                </Typography>
                <Typography sx={{ fontSize: FS.meta, color: 'rgba(255,255,255,0.35)' }}>
                  {new Date(article.published_at).toLocaleString('ja-JP', {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {/* 開いた時点で既読になるので、ここでは解除の口として置く */}
                {/* Chip だと押せることが伝わらないので、枠線付きのボタンにする */}
                <Tooltip title={seen ? '既読を取り消す' : '既読にする'}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onToggleSeen(article.id)}
                    startIcon={seen
                      ? <CheckCircleIcon sx={{ fontSize: 14 }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      py: 0.1,
                      minWidth: 0,
                      fontSize: FS.tag,
                      color: seen ? 'primary.main' : 'rgba(255,255,255,0.6)',
                      borderColor: seen ? 'rgba(217,255,0,0.5)' : 'rgba(255,255,255,0.25)',
                      '& .MuiButton-startIcon': { mr: 0.4 },
                      '&:hover': {
                        borderColor: seen ? 'primary.main' : 'rgba(255,255,255,0.5)',
                      },
                    }}
                  >
                    {seen ? '既読' : '未読'}
                  </Button>
                </Tooltip>
                {article.related_count > 0 && (
                  <Tooltip title="同じ話題の記事">
                    <Chip
                      icon={<LayersIcon sx={{ fontSize: 12 }} />}
                      label={article.related_count + 1}
                      size="small"
                      onClick={() => onOpenRelated(article.id)}
                      sx={{ height: 20, fontSize: FS.tag }}
                    />
                  </Tooltip>
                )}

              </Box>

              {/* 一覧では3行で切っているので、ここでは全文を出す */}
              <Typography sx={{ fontSize: FS.title, fontWeight: 700, lineHeight: 1.5 }}>
                {article.title}
              </Typography>

              {article.summary && (
                <Typography sx={{ fontSize: FS.summary, color: 'rgba(255,255,255,0.6)', mt: 1, lineHeight: 1.6 }}>
                  {article.summary}
                </Typography>
              )}
            </Box>

            {data.games.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                <Box sx={{ px: 2.5, pb: 1 }}>
                  <Typography sx={{ fontSize: FS.label, color: 'rgba(255,255,255,0.45)', mt: 1.5 }}>
                    この記事に登場するゲーム
                  </Typography>
                  {data.games.map((g) => <GameRow key={g.game_id} game={g} />)}
                </Box>
              </>
            )}
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
          <Box sx={{ p: 2, flexShrink: 0 }}>
            {/* ブックマークは見出し横の小さなアイコンだと気づかれない。
                主導線と並べてラベル付きで出す */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={bookmarked ? 'contained' : 'outlined'}
                color={bookmarked ? 'primary' : 'inherit'}
                onClick={() => onToggleBookmark(article)}
                startIcon={bookmarked
                  ? <BookmarkIcon sx={{ fontSize: 16 }} />
                  : <BookmarkBorderIcon sx={{ fontSize: 16 }} />}
                sx={{ flexShrink: 0, fontSize: FS.summary, whiteSpace: 'nowrap' }}
              >
                {bookmarked ? '保存済み' : '保存'}
              </Button>
              <Button
                fullWidth
                variant="contained"
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={openArticle}
              >
                記事を読む
              </Button>
            </Box>
            <Typography sx={{ fontSize: FS.note, color: 'rgba(255,255,255,0.3)', textAlign: 'center', mt: 0.75 }}>
              {article.source.name} のサイトが開きます
            </Typography>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default NewsDetailSheet;
